/**
 * Concurrent damage/heal float scatter — fan-out so stacked hits stay readable.
 * Pure math + cluster bookkeeping (no Phaser).
 */

/** Cluster radius (px): floats within this of each other share a fan. */
export const FLOAT_SCATTER_CLUSTER_PX = 44;
/** Soft cap so numbers stay near the body / on-screen. */
export const FLOAT_SCATTER_MAX_R = 30;
/** Golden angle — even spiral packing. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

export type FloatScatterOffset = { dx: number; dy: number };

type ActiveFloat = { x: number; y: number; until: number };

const active: ActiveFloat[] = [];

/** Slot 0 stays put; later slots spiral outward (slightly Y-squashed). */
export function floatScatterOffset(slot: number): FloatScatterOffset {
  if (slot <= 0) return { dx: 0, dy: 0 };
  const ang = -Math.PI / 2 + slot * GOLDEN_ANGLE;
  const r = Math.min(FLOAT_SCATTER_MAX_R, 11 + Math.sqrt(slot) * 9.5);
  return {
    dx: Math.cos(ang) * r,
    dy: Math.sin(ang) * r * 0.72,
  };
}

function prune(nowMs: number): void {
  for (let i = active.length - 1; i >= 0; i--) {
    if (active[i]!.until <= nowMs) active.splice(i, 1);
  }
}

/**
 * Allocate a scatter offset for a float at (x,y). Nearby recent floats bump the slot.
 * `holdMs` = how long this float occupies the cluster (typically preset.duration).
 */
export function allocateFloatScatter(
  x: number,
  y: number,
  nowMs: number,
  holdMs: number,
): FloatScatterOffset {
  prune(nowMs);
  let slot = 0;
  for (const a of active) {
    if (Math.hypot(a.x - x, a.y - y) <= FLOAT_SCATTER_CLUSTER_PX) slot += 1;
  }
  const off = floatScatterOffset(slot);
  active.push({
    x: x + off.dx,
    y: y + off.dy,
    until: nowMs + Math.max(80, holdMs),
  });
  return off;
}

/** Test / scene-reset helper. */
export function resetFloatScatter(): void {
  active.length = 0;
}
